import { useState, useRef, useEffect, useCallback } from "react";
import { useManagerChat } from "hooks/useManagerChat";
import { useAssistantIdentity } from "hooks/useAssistantIdentity";
import Message from "types/Message";
import React from "react";
import { connectToWebSocket, toCamelCase, uploadFile } from "apiClient";
import { Loader2, Paperclip, Send, X } from "lucide-react";
import { MessageBalloon } from "./MessageBalloon";
import { useAuth } from "context/AuthContext";
import {
  ensureMessageListKey,
  isOptimisticMessage,
  upsertWebsocketMessage,
} from "utils/message";

export function ManagerInstructionsChat() {
  const [page, setPage] = useState(1);
  const [allMessages, setAllMessages] = useState<Message[]>([]);
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const tr = (es: string, en: string) => (locale === "es" ? es : en);
  const { assistantName } = useAssistantIdentity();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { messages, loading, error, next, sendMessage } = useManagerChat(page);

  const loadedPages = useRef<Set<number>>(new Set());
  const socketRef = useRef<ReturnType<typeof connectToWebSocket> | null>(null);

  useEffect(() => {
    if (socketRef.current) {
      socketRef.current.close();
    }

    const socket = connectToWebSocket("/ws/manager/chat/", (event) => {
      const message = ensureMessageListKey(
        toCamelCase(JSON.parse(event.data)) as Message
      );
      setAllMessages((prev) => {
        const filtered = prev.filter((m) => {
          if (!isOptimisticMessage(m)) return true;
          if (m.content !== message.content) return true;
          if (m.fileUrl && !message.fileUrl) return true;
          return false;
        });

        return upsertWebsocketMessage(filtered, message);
      });
    });

    socketRef.current = socket;

    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, []);

  useEffect(() => {
    setAllMessages([]);
    setPage(1);
    loadedPages.current = new Set();
  }, []);

  useEffect(() => {
    if (loading) return;

    const shouldProcess =
      !loadedPages.current.has(page) ||
      (messages.length > 0 && allMessages.length === 0 && page === 1);

    if (!shouldProcess) return;

    if (messages.length > 0) {
      setAllMessages((prev) => {
        const existingKeys = new Set(prev.map((m) => m.listKey));
        const newMessages = messages.filter((m) => !existingKeys.has(m.listKey));
        return [...prev, ...newMessages];
      });
    }

    loadedPages.current.add(page);
  }, [messages, page, loading, allMessages.length]);

  useEffect(() => {
    if (page === 1 && allMessages.length > 0) {
      bottomRef.current?.scrollIntoView({ behavior: "auto" });
    }
  }, [page, allMessages]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || loading || !next) return;

    if (!loadedPages.current.has(page)) return;

    if (container.scrollTop < 50) {
      setPage((prev) => prev + 1);
    }
  }, [loading, next, page]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  const [messageToSend, setMessageToSend] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setFileError(
          tr(
            "El archivo es demasiado grande (máx 50MB)",
            "File is too large (max 50MB)"
          )
        );
        setSelectedFile(null);
      } else {
        setFileError(null);
        setSelectedFile(file);
      }
    }
    e.target.value = "";
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError(null);
  };

  const handleSend = async () => {
    if (uploading) return;

    const content = messageToSend.trim();
    if (!content && !selectedFile) return;

    let fileUrl: string | null = null;
    if (selectedFile) {
      setUploading(true);
      try {
        fileUrl = await uploadFile(selectedFile);
      } catch (err) {
        setFileError(tr("Error subiendo el archivo", "Error uploading file"));
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    const optimisticId = -Date.now();
    const optimisticMessage: Message = {
      id: optimisticId,
      listKey: String(optimisticId),
      type: "user",
      content,
      fileUrl,
      fileName: selectedFile ? selectedFile.name : null,
      sentAt: new Date().toISOString(),
      channel: "fake",
      subject: null,
    };

    setAllMessages((prev) => [optimisticMessage, ...prev]);
    setMessageToSend("");
    setSelectedFile(null);
    setFileError(null);
    const freshMessages = await sendMessage(content, fileUrl);
    if (freshMessages) {
      setAllMessages((prev) => {
        const freshKeys = new Set(freshMessages.map((m) => m.listKey));
        const extras = prev.filter((m) => {
          if (freshKeys.has(m.listKey)) return false;
          if (isOptimisticMessage(m)) {
            return !freshMessages.some(
              (f) =>
                (f.content ?? "") === (m.content ?? "") &&
                (f.fileUrl ?? null) === (m.fileUrl ?? null)
            );
          }
          return true;
        });
        return [...freshMessages, ...extras].sort(
          (a, b) => Date.parse(b.sentAt) - Date.parse(a.sentAt)
        );
      });
      loadedPages.current = new Set([1]);
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const canSendMessage =
    !uploading &&
    (Boolean(messageToSend.trim()) || Boolean(selectedFile));

  return (
    <div className="chat-container flex h-full min-h-0 w-full min-w-0 flex-1 flex-col gap-2 lg:gap-3">
      <div
        ref={scrollContainerRef}
        className="chat-messages-pane min-h-0 flex-1 overflow-y-auto space-y-2 px-2 py-2 lg:space-y-4 lg:px-3 lg:py-3 w-full"
      >
        <div ref={topRef} />
        {error && (
          <div className="text-red-500 text-xs lg:text-sm">{error}</div>
        )}
        {loading && allMessages.length === 0 && (
          <div className="flex justify-center py-12 text-sm text-gray-500">
            {tr("Cargando mensajes…", "Loading messages…")}
          </div>
        )}
        {!loading && allMessages.length === 0 && !error && (
          <div className="flex flex-col items-center py-8 px-6 text-center">
            <p className="text-sm font-medium text-gray-700">
              {tr("Todavía no hay instrucciones", "No instructions yet")}
            </p>
            <p className="mt-2 max-w-sm text-xs text-gray-500">
              {tr(
                `Enviá instrucciones a ${assistantName} como si fueras el administrador. Estos mensajes no aparecen en el historial normal.`,
                `Send instructions to ${assistantName} as the property manager. These messages are hidden from the normal chat history.`
              )}
            </p>
          </div>
        )}
        {[...allMessages].reverse().map((msg) => (
          <MessageBalloon key={msg.listKey} message={msg} contactId={undefined} />
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="flex w-full shrink-0 items-center gap-1 lg:gap-2">
        <textarea
          value={messageToSend}
          onChange={(e) => setMessageToSend(e.target.value)}
          placeholder={tr(
            `Enviar instrucción a ${assistantName}...`,
            `Send instruction to ${assistantName}...`
          )}
          className="flex-1 border border-gray-300 rounded p-2 text-base lg:text-sm resize-none h-10 lg:h-12"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (e.metaKey || e.shiftKey) {
                return;
              }
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={uploading}
        />
        <label className="cursor-pointer flex items-center p-1 lg:p-2">
          <input
            type="file"
            className="hidden"
            onChange={handleFileChange}
            disabled={uploading}
          />
          <Paperclip className="h-4 w-4 lg:h-5 lg:w-5 text-gray-500" />
        </label>
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSendMessage}
          className={`flex shrink-0 items-center justify-center rounded-full p-1 transition-all ${
            canSendMessage || uploading
              ? "text-primary hover:scale-105 hover:text-primary-light active:scale-95"
              : "cursor-not-allowed text-stone-300"
          }`}
          aria-label={
            uploading
              ? tr("Enviando mensaje", "Sending message")
              : tr("Enviar mensaje", "Send message")
          }
          title={uploading ? tr("Enviando...", "Sending...") : tr("Enviar", "Send")}
        >
          {uploading ? (
            <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
          ) : (
            <Send
              className={`h-8 w-8 ${canSendMessage ? "fill-primary/25" : ""}`}
              strokeWidth={canSendMessage ? 2.5 : 2}
              aria-hidden
            />
          )}
        </button>
      </div>
      {selectedFile && (
        <div className="flex items-center mt-1 bg-gray-100 rounded px-2 py-1 text-xs">
          <span className="truncate max-w-xs">{selectedFile.name}</span>
          <button onClick={handleRemoveFile} className="ml-2">
            <X className="h-3 w-3 lg:h-4 lg:w-4 text-gray-400" />
          </button>
        </div>
      )}
      {fileError && (
        <div className="text-xs text-red-500 mt-1">{fileError}</div>
      )}
    </div>
  );
}
